import React, { memo } from 'react';
import { clsx } from 'clsx';
import type { LucideIcon } from 'lucide-react';

export type SectionHeaderProps = {
  eyebrowText?: string;
  eyebrowIcon?: LucideIcon;
  title: string;
  subtitle?: string;
  textColorClass?: string;
  align?: 'left' | 'center';
  className?: string;
  titleGradientClassName?: string;
};

function SectionHeaderBase({
  eyebrowText,
  eyebrowIcon: EyebrowIcon,
  title,
  subtitle,
  textColorClass = 'text-white',
  align = 'center',
  className,
  titleGradientClassName,
}: SectionHeaderProps) {
  const isCenter = align === 'center';

  return (
    <header
      className={clsx(
        'relative max-w-4xl mx-auto',
        isCenter ? 'text-center' : 'text-left',
        'mb-16 sm:mb-20',
        className
      )}
    >
      <div
        aria-hidden="true"
        className={clsx(
          'pointer-events-none absolute top-36 -translate-x-1/2 left-1/2',
          !isCenter && 'left-0 translate-x-0'
        )}
        style={{ willChange: 'transform' }}
      >
        <div
          className="
            w-0 h-0
            shadow-[0_0_180px_110px_rgba(255,255,255,0.10)]
            motion-reduce:shadow-none
          "
        />
      </div>

      {/* {eyebrowText && (
        <div
          className={clsx(
            'relative flex items-center gap-2 w-max px-5 py-2.5 rounded-full mb-6',
            'bg-[#d65c30]',
            'shadow-[0_8px_20px_-4px_rgba(214,92,48,0.55)]',
            'motion-safe:animate-pulse',
            isCenter && 'mx-auto'
          )}
        >
          {EyebrowIcon && <EyebrowIcon
            className="w-4 h-4"
            style={{ color: '#ffffff' }}
          />}
          <span className="text-sm font-montserrat font-semibold text-white">{eyebrowText}</span>
        </div>
      )} */}

      <h2
        className={clsx(
          'relative inline-block font-playfair font-bold',
          'text-5xl sm:text-6xl lg:text-7xl',
          'leading-[1.25] sm:leading-[1.2] lg:leading-[1.2]',
          '[background-origin:padding-box] bg-clip-text text-transparent',
          'bg-[linear-gradient(90deg,#ff7a1a_0%,#ff8f2f_45%,#ffb867_100%)]',
          'overflow-visible'
        )}
      >
        {title}
      </h2>

      {subtitle && (
        <p
          className={clsx(
            'relative font-montserrat text-lg mt-6 max-w-2xl leading-relaxed',
            isCenter ? 'mx-auto' : 'mx-0',
            textColorClass === 'text-white' ? 'text-gray-300' : textColorClass
          )}
        >
          {subtitle}
        </p>
      )}
    </header>
  );
}

export const SectionHeader = memo(SectionHeaderBase, (prevProps, nextProps) => {
  return (
    prevProps.eyebrowText === nextProps.eyebrowText &&
    prevProps.eyebrowIcon === nextProps.eyebrowIcon &&
    prevProps.title === nextProps.title &&
    prevProps.subtitle === nextProps.subtitle &&
    prevProps.textColorClass === nextProps.textColorClass &&
    prevProps.align === nextProps.align &&
    prevProps.className === nextProps.className &&
    prevProps.titleGradientClassName === nextProps.titleGradientClassName
  );
});

SectionHeader.displayName = 'SectionHeader';